import { Environment, Metadata, Package } from '@workspaces/types'
import { getPackageAvailability } from '@/services/packages-browser.service'
import { getPriceValueUpdated, preparePriceModel } from './price.helper'

function classifyAvailability(
  availability: number,
): Package.AvailabilityCategories {
  if (availability < Package.AvailabilityCategories.Yellow) {
    return Package.AvailabilityCategories.Red
  } else if (availability < Package.AvailabilityCategories.Green) {
    return Package.AvailabilityCategories.Yellow
  }
  return Package.AvailabilityCategories.Green
}

function resolvePackagesAvailability(
  packages: Package.PackageDetail[],
): number | null {
  if (packages.length === 0) {
    return null
  }

  const availabilityCategories = new Set<number>()
  for (const pkg of packages) {
    if (pkg.availability != null) {
      availabilityCategories.add(classifyAvailability(pkg.availability))
    }
  }

  if (availabilityCategories.size === 1) {
    const [category] = availabilityCategories
    return category
  }

  return Package.AvailabilityCategories.Yellow
}

export async function getPackagesAvailability(
  appMetadata: Metadata.AppMetadata,
  environment: Environment.EnvironmentResolver,
  packages: Package.PackageDetail[],
  dateRange?: [string, string] | null,
): Promise<number | null> {
  if (!dateRange || dateRange.length !== 2) {
    return null
  }

  if (!packages || packages.length === 0) {
    return null
  }

  const [startDate, endDate] = dateRange
  const availabilityPromises = packages.map((pkg) =>
    getPackageAvailability(
      appMetadata,
      environment,
      startDate,
      endDate,
      pkg.id,
    ),
  )
  const availabilities = await Promise.all(availabilityPromises)

  // Assign availabilities to packages
  packages.forEach((pkg, index) => {
    pkg.availability = availabilities[index]
  })

  const finalAvailability = resolvePackagesAvailability(packages)
  return finalAvailability
}

function castPackageWithAllDataToPackageDetail(
  pkg: Package.PackageWithAllData,
): Package.PackageDetail {
  const digital = pkg.digital ?? false
  const assetAllocation = digital ? 0 : pkg.posting
  const materialHandling = digital ? pkg.posting : 0
  const photography = digital ? 0 : pkg.posting
  return {
    id: pkg.package_id,
    name: pkg.package,
    availability: null,
    originalName: pkg.package,
    digital,
    price: {
      price: pkg.gross_price,
      production: pkg.production,
      posting: pkg.posting,
    },
    customTags: {
      discounts: null,
      fees: null,
    },
    priceDetails: {
      grossPrice: pkg.gross_price,
      discounts: {
        freePercentage: 0,
        mediaAgencyPercentage: 0,
        otherPercentage: 0,
      },
      fees: {
        electricity: 0,
        production: pkg.production,
        assetAllocation,
        materialHandling,
        photography,
        others: 0,
      },
      totalNet:
        pkg.gross_price +
        pkg.production +
        assetAllocation +
        materialHandling +
        photography,
    },
  }
}

export function preparePackageSelection(
  selectedPackages: Package.PackageTableData[],
  allPackages: Package.PackageWithAllData[],
  currentFilter: Package.PlanPackage,
): Package.PlanPackage {
  if (!currentFilter.packages) {
    currentFilter.packages = []
  }

  const currentPackagesSelected = currentFilter.packages
  // Remove from current selection unselected packages
  let currentPackagesSelectedWithoutUnselected = currentPackagesSelected
  if (selectedPackages.length > 0) {
    currentPackagesSelectedWithoutUnselected = currentPackagesSelected.filter(
      (pkg) => selectedPackages.some((selected) => selected.id === pkg.id),
    )
  }

  // Remove from selectedPackages already selected packages in currentFilter
  const selectedPackagesWithoutDuplicates = selectedPackages.filter(
    (pkg) =>
      !currentPackagesSelectedWithoutUnselected.some(
        (selected) => selected.id === pkg.id,
      ),
  )

  // Get data for all new selected packages
  const newPackageSelection = allPackages.filter((pkg) =>
    selectedPackagesWithoutDuplicates.some(
      (selected) => selected.id === pkg.package_id,
    ),
  )
  const packageSelectionAsPackageDetail = newPackageSelection.map((pkg) =>
    castPackageWithAllDataToPackageDetail(pkg),
  )

  // Add new packages to currentFilter.packages
  const finalPackagesSelection = [
    ...currentPackagesSelectedWithoutUnselected,
    ...packageSelectionAsPackageDetail,
  ]

  // Prepare variables to calculate the acummulated values of the price
  let accumulatedGrossPrice = 0
  let accumProduction = 0
  let accumAssetAllocation = 0
  let accumMaterialHandling = 0
  let accumPhotography = 0
  let accummElectricity = 0
  let accummOthers = 0
  let accummTotalNet = 0

  finalPackagesSelection.forEach((data) => {
    accumulatedGrossPrice += data.priceDetails.grossPrice
    accumProduction += data.priceDetails.fees.production
    accumAssetAllocation += !data.digital
      ? data.priceDetails.fees.assetAllocation
      : 0
    accumMaterialHandling += data.digital
      ? data.priceDetails.fees.materialHandling
      : 0
    accumPhotography += !data.digital ? data.priceDetails.fees.photography : 0
    accummElectricity += data.priceDetails.fees.electricity
    accummOthers += data.priceDetails.fees.others
    const totalPriceCalculated = getPriceValueUpdated(data.priceDetails)
    accummTotalNet += totalPriceCalculated.totalNet
  })

  const totalAvailability = resolvePackagesAvailability(finalPackagesSelection)
  const currentFilterPrice = preparePriceModel(currentFilter.price)

  const totalPlanPrice: Package.PlanPackagePrice = {
    ...currentFilterPrice, // We do a copy to keep the discounts set by the user
    grossPrice: accumulatedGrossPrice,
    fees: {
      production: accumProduction,
      assetAllocation: accumAssetAllocation,
      materialHandling: accumMaterialHandling,
      photography: accumPhotography,
      electricity: accummElectricity,
      others: accummOthers,
    },
    totalNet: accummTotalNet,
  }

  return {
    ...currentFilter,
    packages: finalPackagesSelection,
    price: totalPlanPrice,
    assets: 0,
    impressions: 0,
    availability: totalAvailability,
  }
}

export function clearPackageSelection(
  planPackage: Package.PlanPackage,
): Package.PlanPackage {
  return {
    ...planPackage,
    packages: [],
    price: null,
    assets: 0,
    impressions: 0,
    availability: null,
  }
}

/**
 * Updates the impressions and assets count for a plan package
 */
export function updateImpressionsAndAssetsCount(
  updatedFilter: Package.PlanPackage,
  impressionsCount: number,
  assetsCount: number,
): void {
  updatedFilter.assets = assetsCount
  updatedFilter.impressions = impressionsCount
}
